import React, { useEffect, useRef, useState } from 'react';
import type { HistorySnapshots } from '../hooks/useHistorySnapshots';

/**
 * HistoryBar - the Save Design / Undo / Redo control strip shared by
 * every design editor (owner's requirement, 2026-10-26). Save Design
 * keeps the design in this browser; when the editor opted into
 * account saving and the user is signed in, it also writes the
 * design to their account, and the My Designs picker lists, loads
 * and deletes both copies.
 *
 * The picker is an expandable panel in the bar, NOT a select gated
 * on sign-in: the old select only rendered for signed-in users, so
 * the studio menu's "My saved designs" action (CheckBuilder's
 * openMyDesigns) had nothing to surface for a guest who had just
 * saved locally - the click visibly did nothing. The bar now owns
 * the whole interaction: it listens for OPEN_MY_DESIGNS_EVENT,
 * expands the picker, scrolls itself into view and focuses the
 * picker's first control. dispatchOpenMyDesigns() below is the one
 * way other surfaces ask for it.
 */

export const OPEN_MY_DESIGNS_EVENT = 'oc:open-my-designs';

/** Ask the mounted HistoryBar to surface its My Designs picker. */
export function dispatchOpenMyDesigns(): void {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(OPEN_MY_DESIGNS_EVENT));
    }
}

export default function HistoryBar({ history, dark, label }: { history: HistorySnapshots; dark?: boolean; label?: string }) {
    const {
        undo, redo, save, canUndo, canRedo, dirty, savedAt, saveNote,
        accountEnabled, accountDesigns, accountBusy,
        saveToAccount, loadAccountDesign, deleteAccountDesign,
        localSaved, loadLocalSaved
    } = history;
    const [pickerOpen, setPickerOpen] = useState<boolean>(false);
    const barRef = useRef<HTMLDivElement | null>(null);

    // The studio menu's "My saved designs" action lands here: open
    // the picker, bring the bar into view, and put focus in the
    // picker so the answer to the click is unmistakable.
    useEffect(() => {
        const onOpen = () => {
            setPickerOpen(true);
            window.setTimeout(() => {
                const bar = barRef.current;
                if (!bar) return;
                try { bar.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch { /* scroll is best-effort */ }
                const first = bar.querySelector<HTMLElement>('[data-designs-picker] button');
                if (first) { try { first.focus({ preventScroll: true }); } catch { /* focus is a nicety */ } }
            }, 60);
        };
        window.addEventListener(OPEN_MY_DESIGNS_EVENT, onOpen);
        return () => window.removeEventListener(OPEN_MY_DESIGNS_EVENT, onOpen);
    }, []);

    const btn = (enabled: boolean): React.CSSProperties => ({
        padding: '7px 14px',
        borderRadius: 8,
        border: `1px solid ${dark ? 'rgba(255,255,255,0.18)' : '#CBD5E1'}`,
        background: enabled
            ? (dark ? 'rgba(255,255,255,0.10)' : '#FFFFFF')
            : (dark ? 'rgba(255,255,255,0.03)' : '#F1F5F9'),
        color: enabled ? (dark ? '#F1F5F9' : '#0F172A') : (dark ? '#64748B' : '#94A3B8'),
        fontSize: 12,
        fontWeight: 600,
        cursor: enabled ? 'pointer' : 'not-allowed',
        whiteSpace: 'nowrap' as const
    });

    const smallBtn = (enabled: boolean): React.CSSProperties => ({
        ...btn(enabled),
        padding: '4px 10px',
        fontSize: 11
    });

    const status = saveNote
        ? saveNote
        : dirty
            ? 'Unsaved changes'
            : savedAt
                ? `Saved ${new Date(savedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                : 'Not saved yet';

    const onSave = () => {
        save();
        // saveToAccount no-ops for guests and editors without account
        // saving; calling it unconditionally covers users who signed
        // in after the editor mounted.
        saveToAccount();
    };

    const designsCount = accountDesigns.length + (localSaved ? 1 : 0);
    const subText: React.CSSProperties = { fontSize: 11, color: dark ? '#94A3B8' : '#64748B' };
    const rowStyle: React.CSSProperties = {
        display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
        padding: '6px 8px', borderRadius: 8,
        background: dark ? 'rgba(255,255,255,0.05)' : '#FFFFFF',
        border: `1px solid ${dark ? 'rgba(255,255,255,0.10)' : '#E2E8F0'}`
    };
    const when = (at: number) => at
        ? `${new Date(at).toLocaleDateString([], { month: 'short', day: 'numeric' })} · ${new Date(at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
        : '';

    return (
        <div
            ref={barRef}
            data-history-bar
            style={{
                display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                padding: '10px 12px', borderRadius: 10,
                background: dark ? 'rgba(20,22,29,0.92)' : '#F8FAFC',
                border: `1px solid ${dark ? 'rgba(255,255,255,0.12)' : '#E2E8F0'}`
            }}
        >
            {label && (
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', color: dark ? '#94A3B8' : '#64748B', marginRight: 2 }}>
                    {label}
                </span>
            )}
            <button type="button" onClick={undo} disabled={!canUndo} style={btn(canUndo)} title="Undo the last design change (Ctrl+Z)">
                ↩ Undo
            </button>
            <button type="button" onClick={redo} disabled={!canRedo} style={btn(canRedo)} title="Redo the change you undid (Ctrl+Shift+Z)">
                ↪ Redo
            </button>
            <button
                type="button"
                onClick={onSave}
                disabled={accountBusy}
                style={{
                    ...btn(true),
                    background: dark ? '#2563EB' : '#2563EB',
                    border: '1px solid #2563EB',
                    color: '#FFFFFF'
                }}
                title={accountEnabled
                    ? 'Save this design to your account — it will be here on any device you sign in on'
                    : 'Save this design — it will still be here when you come back, until you undo it'}
            >
                💾 Save Design
            </button>
            <button
                type="button"
                data-designs-toggle
                aria-expanded={pickerOpen}
                onClick={() => setPickerOpen(open => !open)}
                style={btn(true)}
                title="Designs you have saved — in this browser and, when you are signed in, in your account"
            >
                📁 My Designs ({designsCount})
            </button>
            <span style={{ fontSize: 11, color: dirty ? (dark ? '#FBBF24' : '#B45309') : (dark ? '#34D399' : '#047857') }}>
                {status}
            </span>
            {pickerOpen && (
                <div
                    data-designs-picker
                    style={{
                        flexBasis: '100%', display: 'flex', flexDirection: 'column', gap: 6,
                        paddingTop: 8, marginTop: 2,
                        borderTop: `1px solid ${dark ? 'rgba(255,255,255,0.10)' : '#E2E8F0'}`
                    }}
                >
                    {localSaved && (
                        <div data-designs-local style={rowStyle}>
                            <span style={{ fontSize: 12, flex: '1 1 auto', minWidth: 0 }}>
                                💾 <strong>{localSaved.name}</strong>{' '}
                                <span style={subText}>Saved in this browser{localSaved.at ? ` · ${when(localSaved.at)}` : ''}</span>
                            </span>
                            <button
                                type="button"
                                data-designs-open-local
                                onClick={() => loadLocalSaved()}
                                style={smallBtn(true)}
                                title="Open the design you saved in this browser"
                            >
                                Open
                            </button>
                        </div>
                    )}
                    {accountEnabled && accountDesigns.map(d => (
                        <div key={d.id} data-designs-account={d.id} style={rowStyle}>
                            <span style={{ fontSize: 12, flex: '1 1 auto', minWidth: 0 }}>
                                ☁️ <strong>{d.name}</strong>{' '}
                                <span style={subText}>{new Date(d.updated_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}</span>
                            </span>
                            <button
                                type="button"
                                disabled={accountBusy}
                                onClick={() => loadAccountDesign(d.id)}
                                style={smallBtn(!accountBusy)}
                                title="Open this design from your account"
                            >
                                Open
                            </button>
                            <button
                                type="button"
                                disabled={accountBusy}
                                onClick={() => deleteAccountDesign(d.id)}
                                style={smallBtn(!accountBusy)}
                                title="Delete this design from your account"
                            >
                                🗑
                            </button>
                        </div>
                    ))}
                    {accountEnabled && accountDesigns.length === 0 && (
                        <div style={subText}>No designs saved to your account yet — Save Design adds the current one.</div>
                    )}
                    {!localSaved && !accountEnabled && (
                        <div style={subText}>No saved designs yet — Save Design keeps your current design here.</div>
                    )}
                    {!accountEnabled && (
                        <div style={subText}>Sign in to also keep designs in your account, on any device.</div>
                    )}
                </div>
            )}
        </div>
    );
}
